/**
 * 사람이 말한 값을 **화면이 이름을 댄 칸**에 곧바로 넣는다.
 *
 * 화면은 모자란 칸을 이름으로 알려 준다(`필수입력확인: 부서를 선택하세요`). 그 이름은
 * 초안 칸의 라벨이고, 사용자는 대개 그 말을 그대로 썼다(`부서는 총무팀`). 그런데도
 * 계획이 그 칸을 안 내는 판이 있다 — 같은 문장으로 돌린 판들이 어떤 때는 `divicd` 를
 * 내고 어떤 때는 통째로 빠뜨렸다. 모델을 한 번 더 부르는 것으로는 안정되지 않는다.
 *
 * 여기서 하는 일은 **추측이 아니다.** 화면이 이름을 대고, 그 이름이 발화에 있고, 그
 * 뒤에 값이 붙어 있을 때만 쓴다. 없으면 아무것도 하지 않는다.
 */

/** 값이 끝나는 자리. 쉼표·마침표·가운뎃점·줄바꿈에서 끊는다. */
const STOP = /[,.·\n;]|그리고|이고|이며/;

/**
 * 값 뒤에 붙는 **지시하는 말**을 뗀다.
 *
 * 사람은 `직위는 통제관으로 넣어줘` 라고 쓴다. 여기서 값은 `통제관` 이지
 * `통제관으로 넣어줘` 가 아니다. 쉼표가 없으니 STOP 이 안 걸리고, 아홉 글자라
 * 길이 제한에도 안 걸린다 — 그대로 칸에 들어간다.
 *
 * 조사 `(으)로` 는 값의 일부일 수 없다. 그 뒤는 언제나 지시다.
 */
const TAIL = /(?:으로|로)?\s*(?:넣어|채워|해|입력해|적어|바꿔|설정해)\s*(?:주세요|줘|줄래|다오|라)?\s*$/;

/**
 * 발화에서 `<이름>는 <값>` 을 뽑는다.
 *
 * 조사는 `는·은·이·가·을·를` 과 `:` `=` 를 받는다. 값이 비면 없는 것으로 본다.
 */
/**
 * 값 끝에 붙은 **말끝**을 뗀다.
 *
 * `자격증번호는 24-1200345야`, `민원구분은 소음이야` 에서 값은 `24-1200345`, `소음` 이다.
 * 그대로 넣으면 화면이 그 글자까지 저장하거나, 목록에 없는 값이 되어 엉뚱하게 막힌다.
 *
 * `이야`·`입니다` 처럼 **두 글자 이상인 말끝은 언제나** 뗀다 — 사람 이름이 그렇게
 * 끝나지 않는다. `야`·`요` 한 글자는 **남는 값이 두 글자 이상일 때만** 뗀다:
 * `이름은 최요` 에서 떼면 이름이 한 글자가 되어 버린다.
 */
const ENDERS = /(?:이야|예요|이에요|입니다|이다)\s*$/;
const SHORT_ENDER = /(?:야|요)\s*$/;

function dropEnder(value: string): string {
  const cut = value.replace(ENDERS, '').trim();
  if (cut !== value.trim()) return cut;
  const short = value.replace(SHORT_ENDER, '').trim();
  return short.length >= 2 ? short : value.trim();
}

export function spokenValue(question: string, label: string): string | undefined {
  const name = label.trim();
  if (!name) return undefined;
  const at = question.indexOf(name);
  if (at < 0) return undefined;
  const raw = question.slice(at + name.length);
  // **조사나 기호를 요구한다.** 띄어쓰기만으로 값을 집으면 뒤에 이어지는 말까지
  // 값이 된다 — `부서 총무팀 으로 넣어줘` 에서 `총무팀 으로 넣어줘` 를 집었다.
  // 사람은 대개 `부서는`·`부서:` 라고 쓴다. 아니면 안 채우는 편이 낫다.
  const marked = /^\s*(?:는|은|이|가|을|를|:|=)\s*/.exec(raw);
  if (!marked) return undefined;
  const rest = raw.slice(marked[0].length);
  const stop = STOP.exec(rest);
  const cut = (stop ? rest.slice(0, stop.index) : rest)
    .trim().replace(TAIL, '').trim();
  const value = dropEnder(cut);
  if (!value) return undefined;
  // 문장이 길게 이어지면 값이 아니라 문장을 집은 것이다.
  return value.length <= 20 ? value : undefined;
}

/**
 * 화면이 이름을 댄 칸들을 발화에서 채운다.
 *
 * 이미 값이 있는 칸은 건드리지 않는다. 채운 칸 이름을 함께 돌려준다 — 아무것도 못
 * 채웠으면 부르는 쪽이 다른 길을 간다.
 */
export function fillSpoken(
  fields: Record<string, string>,
  demandedLabels: string[],
  draftFields: { key: string; label?: string | null }[] | undefined,
  question: string,
): { fields: Record<string, string>; filled: string[] } {
  if (!demandedLabels.length || !draftFields?.length) return { fields, filled: [] };
  const out = { ...fields };
  const filled: string[] = [];
  for (const label of demandedLabels) {
    const field = draftFields.find((one) => (one.label ?? '').trim() === label.trim());
    if (!field) continue;
    if (String(out[field.key] ?? '').trim()) continue;
    const value = spokenValue(question, label);
    if (!value) continue;
    out[field.key] = value;
    filled.push(label);
  }
  return { fields: out, filled };
}

/** 라벨 두 개가 같은 칸을 가리키는가. 화면과 카탈로그가 글자 순서를 바꿔 적는 일이 있다. */
function sameLabel(left: string, right: string): boolean {
  const key = (text: string) => Array.from(text.replace(/\s/g, '')).sort().join('');
  return key(left) === key(right);
}

/**
 * **표 안의 칸**을 발화에서 채운다.
 *
 * 지금까지 이 길은 문서 머리 칸에만 있었다. 그런데 인사등록에서 저장을 막은 것은
 * 언제나 행 안의 칸이었다 — `필수입력확인: 자격증번호를 입력하십시오`. 사용자는
 * `자격증번호는 24-1200345야` 라고 분명히 말했는데 계획이 그 열을 안 냈다(실측
 * 2026-09-01, 여덟 판 중 두 판).
 *
 * 열 이름은 카탈로그의 탭 라벨에서 찾는다(`licencenum` → `자격증번호`). 화면이 댄
 * 이름과 카탈로그 이름이 글자 순서만 다른 일이 있어 정렬해 맞춘다.
 *
 * **없는 값을 만들지 않는다.** 발화에 그 이름이 없으면 아무것도 하지 않는다.
 */
export function fillSpokenRows(
  items: Record<string, string>[],
  demandedLabels: string[],
  labels: Record<string, string> | undefined,
  question: string,
): { items: Record<string, string>[]; filled: string[] } {
  if (!items.length || !demandedLabels.length || !labels) return { items, filled: [] };
  const filled: string[] = [];
  let out = items;
  for (const demanded of demandedLabels) {
    const key = Object.keys(labels).find((column) => sameLabel(labels[column] ?? '', demanded));
    if (!key) continue;
    const value = spokenValue(question, demanded);
    if (!value) continue;
    let touched = false;
    out = out.map((item) => {
      if (String(item[key] ?? '').trim()) return item;
      touched = true;
      return { ...item, [key]: value };
    });
    if (touched) filled.push(demanded);
  }
  return { items: out, filled };
}
